import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { AuthService, AuthSessionService } from '../services';
import { initialAuthState, LoginRequest } from '../models';
import { mapAuthError } from '../utils';

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialAuthState),
  withMethods((store, authService = inject(AuthService), sessionService = inject(AuthSessionService)) => ({
    hydrateSession(): void {
      const session = sessionService.getSession();

      if (!session) {
        return;
      }

      patchState(store, {
        user: session.user,
        isAuthenticated: true,
        accessToken: session.accessToken,
        expiresAt: session.expiresAt,
        error: null,
      });
    },

    async login(request: LoginRequest): Promise<boolean> {
      if (store.isLoading()) {
        return false;
      }

      patchState(store, {
        isLoading: true,
        error: null,
      });

      try {
        const response = await firstValueFrom(authService.login(request));
        const nextState = sessionService.saveSession(response, request.rememberMe);

        patchState(store, nextState);
        return true;
      } catch (error: unknown) {
        patchState(store, {
          user: null,
          isAuthenticated: false,
          accessToken: undefined,
          expiresAt: undefined,
          error: mapAuthError(error),
        });

        return false;
      } finally {
        patchState(store, { isLoading: false });
      }
    },

    logout(): void {
      sessionService.clearSession();
      patchState(store, initialAuthState);
    },

    clearError(): void {
      patchState(store, { error: null });
    },
  })),
);
